/**
Copyright (C) 2022 by SeQuenX  - All Rights Reserved
This file is part of the ComplyVantage product development,
and is released under the "Commercial License Agreement".
You should have received a copy of the Commercial License Agreement license with
this file. If not, please write to: legal@sequenx.com, or visit www.sequenx.com
*/
import React, { useState } from "react";
import { compliance_security } from "../assets/svg";
import { useTranslation } from "react-i18next";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import { motion } from "framer-motion";

// DetailItem Component
const DetailItem = ({ title, description, onMoreDetails, index }) => {
  const colors = [
    "from-slate-700 to-slate-800",
    "from-slate-600 to-slate-700",
    "from-slate-800 to-slate-900",
    "from-slate-700 to-slate-800",
  ];

  const icons = ["🛡️", "📚", "🔒", "⚡"];

  return (
    <motion.div
      whileHover={{ scale: 1.02, y: -5 }}
      className={`bg-gradient-to-br ${
        colors[index % colors.length]
      } rounded-xl p-6 mb-4 shadow-lg transition-all duration-300 hover:shadow-2xl border border-white/10`}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center mb-3">
            <div className="text-3xl mr-3">{icons[index % icons.length]}</div>
            <h3 className="text-xl font-semibold text-white">{title}</h3>
          </div>
          <div className="h-px bg-white/20 mb-4"></div>
        </div>
        <Button
          variant="contained"
          onClick={() => onMoreDetails(title, description)}
          className="bg-white/20 text-white hover:bg-white/30 border border-white/30 backdrop-blur-sm transition-all duration-300 hover:scale-105"
          sx={{
            borderRadius: "12px",
            textTransform: "none",
            fontWeight: 600,
            padding: "8px 20px",
            "&:hover": {
              backgroundColor: "rgba(255, 255, 255, 0.3)",
              transform: "scale(1.05)",
            },
          }}
        >
          Learn More
        </Button>
      </div>
    </motion.div>
  );
};

// AlertDialog Component
const AlertDialog = ({ open, onClose, title, description }) => {
  const { t } = useTranslation();
  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      maxWidth="md"
      fullWidth
    >
      <DialogTitle
        id="alert-dialog-title"
        sx={{
          fontWeight: "bold",
          color: "#455590",
          fontSize: "1.5rem",
        }}
      >
        {title}
      </DialogTitle>
      <DialogContent>
        <DialogContentText id="alert-dialog-description">
          {title === "OSPO services and Security Vulnerabilities." ? (
            <div className="space-y-6">
              <div>
                <h2 className="text-xl font-bold text-[#455590] mb-3">
                  {t("OSSCSS.responsibilities")}
                </h2>
                <p className="text-gray-700">
                  {t("OSSCSS.OSPOServiceSecurityVuln.list")}
                </p>
              </div>
              <div>
                <h2 className="text-xl font-bold text-[#455590] mb-3">
                  {t("OSSCSS.OSPOServiceSecurityVuln.list1")}
                </h2>
                <p className="text-gray-700">
                  {t("OSSCSS.OSPOServiceSecurityVuln.list2")}
                </p>
                <ul className="list-disc pl-6 space-y-2 mt-3">
                  <li className="text-gray-700">
                    {t("OSSCSS.OSPOServiceSecurityVuln.list3")}
                  </li>
                  <li className="text-gray-700">
                    {t("OSSCSS.OSPOServiceSecurityVuln.list4")}
                  </li>
                  <li className="text-gray-700">
                    {t("OSSCSS.OSPOServiceSecurityVuln.list5")}
                  </li>
                  <li className="text-gray-700">
                    {t("OSSCSS.OSPOServiceSecurityVuln.list6")}
                  </li>
                </ul>
                <p className="text-gray-700 mt-3">
                  {t("OSSCSS.OSPOServiceSecurityVuln.list7")}
                </p>
              </div>
              <div>
                <h2 className="text-xl font-bold text-[#455590] mb-3">
                  {t("OSSCSS.OSPOServiceSecurityVuln.list8")}
                </h2>
                <ul className="list-disc pl-6 space-y-2">
                  <li className="text-gray-700">
                    {t("OSSCSS.OSPOServiceSecurityVuln.list9")}
                  </li>
                  <li className="text-gray-700">
                    {t("OSSCSS.OSPOServiceSecurityVuln.list10")}
                  </li>
                  <li className="text-gray-700">
                    {t("OSSCSS.OSPOServiceSecurityVuln.list11")}
                  </li>
                  <li className="text-gray-700">
                    {t("OSSCSS.OSPOServiceSecurityVuln.list12")}
                  </li>
                </ul>
              </div>
            </div>
          ) : title === "OSS Management Training & Coaching." ? (
            <div className="space-y-6">
              <h2 className="text-xl font-bold text-[#455590] mb-3">
                {t("OSSCSS.responsibilities")}
              </h2>
              <ul className="list-disc pl-6 space-y-3">
                <li className="text-gray-700">
                  {t("OSSCSS.ossManagementTraining.list")}
                </li>
                <ul className="list-disc pl-6 space-y-2 mt-2">
                  <li className="text-gray-700">
                    {t("OSSCSS.ossManagementTraining.list1")}
                  </li>
                  <li className="text-gray-700">
                    {t("OSSCSS.ossManagementTraining.list2")}
                  </li>
                  <li className="text-gray-700">
                    {t("OSSCSS.ossManagementTraining.list3")}
                  </li>
                </ul>
                <li className="text-gray-700">
                  {t("OSSCSS.ossManagementTraining.list4")}
                </li>
                <li className="text-gray-700">
                  {t("OSSCSS.ossManagementTraining.list5")}
                </li>
                <li className="text-gray-700">
                  {t("OSSCSS.ossManagementTraining.list6")}
                </li>
              </ul>
            </div>
          ) : (
            <div className="space-y-6">
              <h2 className="text-xl font-bold text-[#455590] mb-3">
                {t("OSSCSS.responsibilities")}
              </h2>
              <ul className="list-disc pl-6 space-y-3">
                {description?.map((detail, index) => (
                  <li key={index} className="text-gray-700">
                    {detail}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={onClose}
          variant="contained"
          className="bg-[#455590] text-white hover:bg-[#455590]/90"
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// OSSCSServiceDetails Component
const OSSCSServiceDetails = () => {
  const { t } = useTranslation();
  const processDetails =
    t("OSSCSS.whatWeDo.details", { returnObjects: true }) || [];
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedDetail, setSelectedDetail] = useState({
    title: "",
    description: [],
  });

  const handleMoreDetailsClick = (title, description) => {
    setSelectedDetail({ title, description });
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white pt-24">
      {/* Page Title */}
      <div className="text-center mb-12">
        <h1 className="text-4xl font-bold text-gray-800 mb-4">
          {t("OSSCSS.title")}
        </h1>
        <div className="w-24 h-1 bg-blue-600 mx-auto"></div>
      </div>

      {/* Hero Section */}
      <div className="container mx-auto px-4 py-12">
        <div className="flex flex-col md:flex-row items-center gap-12">
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            className="w-full md:w-1/2"
          >
            <img
              src={compliance_security}
              alt={t("OSSCSS.title")}
              className="w-full max-w-lg mx-auto"
            />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="w-full md:w-1/2"
          >
            <h2 className="text-3xl font-bold mb-6 text-gray-800">
              {t("OSSCSS.introduction.h1")}
            </h2>
            <div className="space-y-4 text-lg text-gray-700">
              <p>{t("OSSCSS.introduction.paragraphs")}</p>
              <p>{t("OSSCSS.introduction.paragraphs1")}</p>
              <p>{t("OSSCSS.introduction.paragraphs2")}</p>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Services Section */}
      <div className="container mx-auto px-4 py-16">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl font-bold text-gray-800 mb-4">
            {t("OSSCSS.whatWeDo.h1")}
          </h2>
          <div className="w-20 h-1 bg-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-700 max-w-2xl mx-auto">
            {t("OSSCSS.paragraphs")}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {processDetails.map((detail, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <DetailItem
                title={detail.title}
                description={detail.description}
                onMoreDetails={handleMoreDetailsClick}
                index={index}
              />
            </motion.div>
          ))}
        </div>
      </div>

      <AlertDialog
        open={dialogOpen}
        onClose={handleDialogClose}
        title={selectedDetail.title}
        description={selectedDetail.description}
      />
    </div>
  );
};

export default OSSCSServiceDetails;
